// The location detail panel (task-033): a dialog overlay a visitor opens by
// selecting a pin or list row.
//
// It renders a LocationDetail — name, address, open-now status, contact links,
// description, opening hours, custom fields, a photo strip and directions deep
// links — over the shell. The component owns the FETCH (and its abort/retry
// lifecycle); this class owns only the RENDER and the dialog's a11y.
//
// SECURITY (house rule): every customer-supplied URL (website, image URLs) is
// scheme-guarded via util/url before it becomes an href — http/https only, and
// `javascript:`/`data:` are dropped (no anchor rendered at all). Directions
// links are built by us from the numeric lat/lng, so they can't carry a hostile
// scheme; we still construct them via URL/URLSearchParams for correct encoding.
// Broken images hide themselves on error rather than showing a torn-image icon.
//
// HONESTY (house rule): open-now is shown ONLY for a definite OPEN/CLOSED, always
// tagged with the store's timezone so it reads as the STORE's local time, not the
// visitor's. When hours/timezone are unknown we say "Hours unavailable" — never a
// fabricated "open".
//
// A11Y: role="dialog" aria-modal, labelled by the name heading; focus moves into
// the panel on open and is restored to the opener on close; Escape and a Close
// button both dismiss; Tab is trapped within the panel.

import type { CustomField, LocationDetail } from "../data/types";
import { mailtoHref, safeHttpUrl, telHref } from "../util/url";

/** Callback into the component when the visitor dismisses the panel. */
export interface DetailPanelHandlers {
  onClose(): void;
}

/** Days rendered Mon..Sun (canonical lowercase keys match the server hours map). */
const DAY_ORDER: ReadonlyArray<{ key: string; label: string }> = [
  { key: "mon", label: "Mon" },
  { key: "tue", label: "Tue" },
  { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" },
  { key: "fri", label: "Fri" },
  { key: "sat", label: "Sat" },
  { key: "sun", label: "Sun" },
];

export class DetailPanel {
  readonly element: HTMLElement;

  private readonly panel: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly bodyEl: HTMLElement;
  private readonly closeButton: HTMLButtonElement;
  /** The element focused before the panel opened, restored on close. */
  private previouslyFocused: HTMLElement | null = null;
  private static idCounter = 0;

  constructor(private readonly handlers: DetailPanelHandlers) {
    const instanceId = `sd-detail-${++DetailPanel.idCounter}`;

    this.element = div("detail-overlay");
    this.element.hidden = true;
    // Backdrop click (outside the panel) dismisses.
    this.element.addEventListener("click", (event) => {
      if (event.target === this.element) {
        this.requestClose();
      }
    });
    this.element.addEventListener("keydown", (event) => this.onKeyDown(event));

    this.panel = div("detail-panel");
    this.panel.setAttribute("role", "dialog");
    this.panel.setAttribute("aria-modal", "true");
    this.panel.setAttribute("aria-labelledby", `${instanceId}-title`);

    const header = div("detail-header");
    this.titleEl = tag("h2", "detail-title", "");
    this.titleEl.id = `${instanceId}-title`;
    this.closeButton = document.createElement("button");
    this.closeButton.type = "button";
    this.closeButton.className = "detail-close";
    this.closeButton.textContent = "Close";
    this.closeButton.setAttribute("aria-label", "Close location details");
    this.closeButton.addEventListener("click", () => this.requestClose());
    header.append(this.titleEl, this.closeButton);

    this.bodyEl = div("detail-body");

    this.panel.append(header, this.bodyEl);
    this.element.appendChild(this.panel);
  }

  /** Shows the overlay in a loading state and moves focus into the dialog. */
  open(): void {
    const root = this.element.getRootNode();
    const active = root instanceof ShadowRoot || root instanceof Document ? root.activeElement : null;
    this.previouslyFocused = active instanceof HTMLElement ? active : null;

    this.element.hidden = false;
    this.titleEl.textContent = "Loading";
    this.bodyEl.replaceChildren(tag("p", "detail-loading", "Loading location details."));
    this.closeButton.focus();
  }

  /** Fills the panel with a fetched detail. */
  renderDetail(detail: LocationDetail): void {
    this.titleEl.textContent = detail.name;
    this.bodyEl.replaceChildren(...this.buildSections(detail));
  }

  /**
   * Replaces the body with an honest inline error + retry (never crashes the
   * widget). [onRetry] re-runs the fetch; the panel stays open meanwhile.
   */
  renderDetailError(message: string, onRetry: () => void): void {
    this.titleEl.textContent = "Details unavailable";
    const wrap = div("detail-error");
    wrap.setAttribute("role", "alert");
    wrap.appendChild(tag("p", "detail-error-body", message));
    const retry = document.createElement("button");
    retry.type = "button";
    retry.className = "detail-retry";
    retry.textContent = "Try again";
    retry.addEventListener("click", onRetry);
    wrap.appendChild(retry);
    this.bodyEl.replaceChildren(wrap);
  }

  /** Whether the panel is currently open (used to guard stale updates). */
  isOpen(): boolean {
    return !this.element.hidden;
  }

  private requestClose(): void {
    this.close();
    this.handlers.onClose();
  }

  /** Hides the overlay and restores focus to the opener. */
  close(): void {
    if (this.element.hidden) {
      return;
    }
    this.element.hidden = true;
    this.bodyEl.replaceChildren();
    this.previouslyFocused?.focus();
    this.previouslyFocused = null;
  }

  private buildSections(detail: LocationDetail): HTMLElement[] {
    const sections: HTMLElement[] = [];

    const address = composeAddress(detail);
    if (address != null) {
      sections.push(tag("p", "detail-address", address));
    }

    const badge = openNowBadge(detail);
    if (badge != null) {
      sections.push(badge);
    }

    const contact = this.buildContact(detail);
    if (contact != null) {
      sections.push(contact);
    }

    if (detail.description != null && detail.description.trim() !== "") {
      sections.push(tag("p", "detail-description", detail.description));
    }

    const hours = this.buildHours(detail);
    if (hours != null) {
      sections.push(hours);
    }

    const fields = this.buildCustomFields(detail.customFields);
    if (fields != null) {
      sections.push(fields);
    }

    const images = this.buildImages(detail);
    if (images != null) {
      sections.push(images);
    }

    sections.push(this.buildDirections(detail));
    return sections;
  }

  /** Click-to-call / email / website — each only when its guard yields an href. */
  private buildContact(detail: LocationDetail): HTMLElement | null {
    const links: HTMLAnchorElement[] = [];

    const tel = telHref(detail.phone);
    if (tel != null && detail.phone != null) {
      links.push(this.link(tel, detail.phone, "phone", false));
    }
    const mail = mailtoHref(detail.email);
    if (mail != null && detail.email != null) {
      links.push(this.link(mail, detail.email, "email", false));
    }
    const site = safeHttpUrl(detail.website);
    if (site != null && detail.website != null) {
      links.push(this.link(site, detail.website, "website", true));
    }

    if (links.length === 0) {
      return null;
    }
    const wrap = div("detail-contact");
    wrap.append(...links);
    return wrap;
  }

  private link(href: string, text: string, kind: string, external: boolean): HTMLAnchorElement {
    const anchor = document.createElement("a");
    anchor.className = "detail-link";
    anchor.href = href;
    anchor.textContent = text;
    anchor.dataset.kind = kind;
    if (external) {
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
    }
    return anchor;
  }

  /** The Mon..Sun hours table, or null when the store publishes no hours. */
  private buildHours(detail: LocationDetail): HTMLElement | null {
    if (Object.keys(detail.hours).length === 0) {
      return null;
    }
    const section = div("detail-section");
    section.appendChild(tag("h3", "detail-heading", "Hours"));

    const table = document.createElement("table");
    table.className = "detail-hours";
    const today = todayKey(detail.timezone);
    for (const day of DAY_ORDER) {
      const row = document.createElement("tr");
      if (day.key === today) {
        row.className = "is-today";
      }
      const th = tag("th", "detail-hours-day", day.label);
      th.setAttribute("scope", "row");
      const td = tag("td", "detail-hours-value", formatRanges(detail.hours[day.key]));
      row.append(th, td);
      table.appendChild(row);
    }
    section.appendChild(table);
    return section;
  }

  private buildCustomFields(fields: CustomField[]): HTMLElement | null {
    if (fields.length === 0) {
      return null;
    }
    const wrap = div("detail-fields");
    for (const field of fields) {
      const row = div("detail-field");
      row.append(tag("span", "detail-field-label", field.label), tag("span", "detail-field-value", field.value));
      wrap.appendChild(row);
    }
    return wrap;
  }

  /** A horizontal photo strip; each image hides itself if it fails to load. */
  private buildImages(detail: LocationDetail): HTMLElement | null {
    const urls = detail.images.map((raw) => safeHttpUrl(raw)).filter((url): url is string => url != null);
    if (urls.length === 0) {
      return null;
    }
    const strip = div("detail-images");
    for (const url of urls) {
      const img = document.createElement("img");
      img.className = "detail-image";
      img.loading = "lazy";
      img.alt = detail.name;
      img.src = url;
      // Graceful broken-image hiding — never a torn-image glyph. Markup contract
      // is stable so task-035 can later swap in resized serving.
      img.addEventListener("error", () => {
        img.hidden = true;
      });
      strip.appendChild(img);
    }
    return strip;
  }

  /**
   * Directions deep links, built by us from the numeric lat/lng (so inherently
   * scheme-safe) via URL/URLSearchParams for correct encoding.
   */
  private buildDirections(detail: LocationDetail): HTMLElement {
    const section = div("detail-section");
    section.appendChild(tag("h3", "detail-heading", "Directions"));
    const wrap = div("detail-directions");
    wrap.append(
      this.directionLink("Google Maps", googleDirections(detail.lat, detail.lng)),
      this.directionLink("Apple Maps", appleDirections(detail.lat, detail.lng)),
      this.directionLink("Waze", wazeDirections(detail.lat, detail.lng)),
    );
    section.appendChild(wrap);
    return section;
  }

  private directionLink(label: string, href: string): HTMLAnchorElement {
    const anchor = document.createElement("a");
    anchor.className = "detail-direction";
    anchor.href = href;
    anchor.textContent = label;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    return anchor;
  }

  /** Escape closes; Tab is trapped so focus can't leave the modal dialog. */
  private onKeyDown(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.preventDefault();
      this.requestClose();
      return;
    }
    if (event.key !== "Tab") {
      return;
    }
    const focusables = this.panel.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])',
    );
    if (focusables.length === 0) {
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const root = this.element.getRootNode();
    const active = root instanceof ShadowRoot || root instanceof Document ? root.activeElement : null;
    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

/** Composes a single address line, skipping null/blank parts. */
function composeAddress(detail: LocationDetail): string | null {
  const parts = [detail.address, detail.city, detail.region, detail.postalCode, detail.countryCode].filter(
    (part): part is string => part != null && part.trim() !== "",
  );
  return parts.length > 0 ? parts.join(", ") : null;
}

/**
 * The open-now badge. OPEN/CLOSED are shown with the store's timezone so it reads
 * as the store's local time; unknown states get an honest "Hours unavailable"
 * rather than a fabricated status.
 */
function openNowBadge(detail: LocationDetail): HTMLElement | null {
  if (detail.openNow === "OPEN" || detail.openNow === "CLOSED") {
    const badge = div("detail-badge");
    badge.classList.add(detail.openNow === "OPEN" ? "is-open" : "is-closed");
    const label = detail.openNow === "OPEN" ? "Open now" : "Closed";
    badge.textContent = detail.timezone != null ? `${label} · ${detail.timezone}` : label;
    return badge;
  }
  const badge = div("detail-badge");
  badge.classList.add("is-unknown");
  badge.textContent = "Hours unavailable";
  return badge;
}

/** Renders a day's ranges as "09:00–17:00" (comma-joined), or "Closed". */
function formatRanges(ranges: string[][] | undefined): string {
  if (ranges == null || ranges.length === 0) {
    return "Closed";
  }
  return ranges.map((range) => `${range[0]}–${range[1]}`).join(", ");
}

/**
 * Today's canonical day key in the STORE's timezone (so "today" is honest for a
 * visitor in a different timezone). Falls back to the visitor's own day if the
 * timezone is unknown or rejected by the platform.
 */
function todayKey(timezone: string | null): string {
  try {
    const formatted = new Intl.DateTimeFormat("en-US", {
      weekday: "short",
      timeZone: timezone ?? undefined,
    }).format(new Date());
    return formatted.toLowerCase().slice(0, 3);
  } catch {
    return new Intl.DateTimeFormat("en-US", { weekday: "short" }).format(new Date()).toLowerCase().slice(0, 3);
  }
}

function googleDirections(lat: number, lng: number): string {
  const url = new URL("https://www.google.com/maps/dir/");
  url.searchParams.set("api", "1");
  url.searchParams.set("destination", `${lat},${lng}`);
  return url.href;
}

function appleDirections(lat: number, lng: number): string {
  const url = new URL("https://maps.apple.com/");
  url.searchParams.set("daddr", `${lat},${lng}`);
  return url.href;
}

function wazeDirections(lat: number, lng: number): string {
  const url = new URL("https://waze.com/ul");
  url.searchParams.set("ll", `${lat},${lng}`);
  url.searchParams.set("navigate", "yes");
  return url.href;
}

function div(className: string): HTMLElement {
  const node = document.createElement("div");
  node.className = className;
  return node;
}

function tag(name: string, className: string, content: string): HTMLElement {
  const node = document.createElement(name);
  node.className = className;
  node.textContent = content;
  return node;
}
